import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
} from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  createServiceAdmin,
  deleteServiceAdmin,
  getServiceDetailsAdmin,
  updateServiceAdmin,
} from "../../actions/serviceActions";
import {
  ADMIN_SERVICE_CREATE_RESET,
  ADMIN_SERVICE_DELETE_RESET,
  ADMIN_SERVICE_UPDATE_RESET,
} from "../../constants/serviceConstants";
import Loader from "../../components/Loader";
import Message from "../../components/Message";

function formatPricingOptions(options = []) {
  return (options || [])
    .map((option) =>
      [
        option.key || "",
        option.label || "",
        option.price ?? "",
        option.unitLabel || "",
        option.minQuantity ?? 1,
        option.maxQuantity ?? 100,
        option.isDefault ? "default" : "",
        option.image || "",
        Array.isArray(option.backOptions) ? option.backOptions.join(", ") : "",
        option.includedQuantity ?? "",
        option.includedItemLabel || "",
      ].join(" | "),
    )
    .join("\n");
}

function parsePricingOptions(text = "") {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [
        key = "",
        label = "",
        price = "0",
        unitLabel = "item",
        minQuantity = "1",
        maxQuantity = "100",
        defaultFlag = "",
        image = "",
        backOptions = "",
        includedQuantity = "",
        includedItemLabel = "",
      ] = line.split("|").map((part) => part.trim());

      return {
        key,
        label,
        price: Number(price) || 0,
        unitLabel,
        minQuantity: Number(minQuantity) || 1,
        maxQuantity: Number(maxQuantity) || 100,
        isDefault: defaultFlag.toLowerCase() === "default",
        isActive: true,
        image,
        backOptions: backOptions
          .split(/[,;\n\r]+/)
          .map((item) => item.trim())
          .filter(Boolean),
        includedQuantity: Number(includedQuantity) || 0,
        includedItemLabel,
      };
    });
}

export default function AdminServiceFormScreen() {
  const { id } = useParams();
  const isEdit = Boolean(id);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const details = useSelector((state) => state.adminServiceDetails);
  const { loading: loadingDetails, error: errorDetails, service } = details;

  const created = useSelector((state) => state.adminServiceCreate);
  const {
    loading: loadingCreate,
    success: successCreate,
    error: errorCreate,
  } = created;

  const updated = useSelector((state) => state.adminServiceUpdate);
  const {
    loading: loadingUpdate,
    success: successUpdate,
    error: errorUpdate,
  } = updated;

  const deleted = useSelector((state) => state.adminServiceDelete);
  const {
    loading: loadingDelete,
    success: successDelete,
    error: errorDelete,
  } = deleted;

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const [detailsText, setDetailsText] = useState("");
  const [priceLabel, setPriceLabel] = useState("Price on request");
  const [currency, setCurrency] = useState("GBP");
  const [basePrice, setBasePrice] = useState(0);
  const [unitLabel, setUnitLabel] = useState("item");
  const [minQuantity, setMinQuantity] = useState(1);
  const [maxQuantity, setMaxQuantity] = useState(100);
  const [pricingOptionsText, setPricingOptionsText] = useState("");
  const [outboundDeliveryPrice, setOutboundDeliveryPrice] = useState(4.95);
  const [orderable, setOrderable] = useState(true);
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [leadTimeDays, setLeadTimeDays] = useState(1);
  const [order, setOrder] = useState(0);
  const [isPublished, setIsPublished] = useState(true);
  const [isDeleted, setIsDeleted] = useState(false);

  const detailsArray = useMemo(
    () =>
      detailsText
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean),
    [detailsText],
  );

  useEffect(() => {
    if (isEdit) {
      dispatch(getServiceDetailsAdmin(id));
    } else {
      dispatch({ type: ADMIN_SERVICE_CREATE_RESET });
      dispatch({ type: ADMIN_SERVICE_UPDATE_RESET });
      dispatch({ type: ADMIN_SERVICE_DELETE_RESET });
    }
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    if (!isEdit || !service) return;

    setName(service.name || "");
    setSlug(service.slug || "");
    setShortDescription(service.shortDescription || "");
    setDescription(service.description || "");
    setDetailsText((service.details || []).join("\n"));
    setPriceLabel(service.priceLabel || "Price on request");
    setCurrency(service.currency || "GBP");
    setBasePrice(Number(service.basePrice || 0));
    setUnitLabel(service.unitLabel || "item");
    setMinQuantity(Number(service.minQuantity || 1));
    setMaxQuantity(Number(service.maxQuantity || 100));
    setPricingOptionsText(formatPricingOptions(service.pricingOptions || []));
    setOutboundDeliveryPrice(Number(service.outboundDeliveryPrice ?? 4.95));
    setOrderable(service.orderable !== false);
    setDurationMinutes(Number(service.durationMinutes || 30));
    setLeadTimeDays(Number(service.leadTimeDays || 1));
    setOrder(Number(service.order || 0));
    setIsPublished(Boolean(service.isPublished));
    setIsDeleted(Boolean(service.isDeleted));
  }, [isEdit, service]);

  useEffect(() => {
    if (successCreate) {
      dispatch({ type: ADMIN_SERVICE_CREATE_RESET });
      navigate("/admin/services");
    }
  }, [dispatch, navigate, successCreate]);

  useEffect(() => {
    if (successUpdate) {
      dispatch({ type: ADMIN_SERVICE_UPDATE_RESET });
      navigate("/admin/services");
    }
  }, [dispatch, navigate, successUpdate]);

  useEffect(() => {
    if (successDelete) {
      dispatch({ type: ADMIN_SERVICE_DELETE_RESET });
      navigate("/admin/services");
    }
  }, [dispatch, navigate, successDelete]);

  const submitHandler = (event) => {
    event.preventDefault();

    const payload = {
      name: name.trim(),
      slug: slug.trim(),
      shortDescription: shortDescription.trim(),
      description: description.trim(),
      details: detailsArray,
      priceLabel: priceLabel.trim(),
      currency: currency.trim().toUpperCase() || "GBP",
      basePrice: Number(basePrice) || 0,
      unitLabel: unitLabel.trim() || "item",
      minQuantity: Number(minQuantity) || 1,
      maxQuantity: Number(maxQuantity) || 100,
      pricingOptions: parsePricingOptions(pricingOptionsText),
      outboundDeliveryPrice: Number(outboundDeliveryPrice) || 0,
      orderable,
      durationMinutes: Number(durationMinutes) || 30,
      leadTimeDays: Number(leadTimeDays) || 0,
      order: Number(order) || 0,
      isPublished,
      ...(isEdit ? { isDeleted } : {}),
    };

    if (isEdit) {
      dispatch(updateServiceAdmin(id, payload));
    } else {
      dispatch(createServiceAdmin(payload));
    }
  };

  const deleteHandler = () => {
    if (!isEdit) return;

    if (window.confirm("Delete this service? It will be unpublished.")) {
      dispatch(deleteServiceAdmin(id));
    }
  };

  const busy = loadingCreate || loadingUpdate || loadingDelete;

  return (
    <Container className="py-4">
      <Row className="align-items-center g-3 mb-3">
        <Col>
          <h1>{isEdit ? "Edit Service" : "Create Service"}</h1>

          {isEdit ? (
            <div className="d-flex gap-2 flex-wrap">
              <Badge bg={isDeleted ? "secondary" : "dark"}>
                {isDeleted ? "Deleted" : "Active"}
              </Badge>
              <Badge
                bg={isPublished ? "success" : "warning"}
                text={isPublished ? undefined : "dark"}
              >
                {isPublished ? "Published" : "Unpublished"}
              </Badge>
            </div>
          ) : null}
        </Col>

        <Col md="auto" className="d-flex gap-2 flex-wrap">
          <Link to="/admin/services" className="btn btn-light">
            Back
          </Link>
          {isEdit ? (
            <Button
              variant="outline-danger"
              onClick={deleteHandler}
              disabled={busy || isDeleted}
            >
              Delete
            </Button>
          ) : null}
        </Col>
      </Row>

      {loadingDetails && <Loader />}
      {errorDetails && <Message variant="danger">{errorDetails}</Message>}
      {errorCreate && <Message variant="danger">{errorCreate}</Message>}
      {errorUpdate && <Message variant="danger">{errorUpdate}</Message>}
      {errorDelete && <Message variant="danger">{errorDelete}</Message>}

      {(!isEdit || service) && (
        <Form onSubmit={submitHandler}>
          <Card className="shadow-sm">
            <Card.Body>
              <Row className="g-3">
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      value={name}
                      onChange={(event) => setName(event.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Slug</Form.Label>
                    <Form.Control
                      value={slug}
                      onChange={(event) => setSlug(event.target.value)}
                      placeholder="generated from name"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Short Description</Form.Label>
                <Form.Control
                  value={shortDescription}
                  onChange={(event) => setShortDescription(event.target.value)}
                  maxLength={240}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Details</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={detailsText}
                  onChange={(event) => setDetailsText(event.target.value)}
                  placeholder="One detail per line"
                />
              </Form.Group>

              <Row className="g-3">
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Price Label</Form.Label>
                    <Form.Control
                      value={priceLabel}
                      onChange={(event) => setPriceLabel(event.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>Currency</Form.Label>
                    <Form.Control
                      value={currency}
                      maxLength={3}
                      onChange={(event) => setCurrency(event.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Base Price</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={basePrice}
                      onChange={(event) => setBasePrice(event.target.value)}
                    />
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Unit Label</Form.Label>
                    <Form.Control
                      value={unitLabel}
                      onChange={(event) => setUnitLabel(event.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-3">
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Minimum Quantity</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="1000"
                      value={minQuantity}
                      onChange={(event) => setMinQuantity(event.target.value)}
                    />
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Maximum Quantity</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max="1000"
                      value={maxQuantity}
                      onChange={(event) => setMaxQuantity(event.target.value)}
                    />
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Return Shipping Price</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={outboundDeliveryPrice}
                      onChange={(event) =>
                        setOutboundDeliveryPrice(event.target.value)
                      }
                    />
                  </Form.Group>
                </Col>

                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Duration Minutes</Form.Label>
                    <Form.Control
                      type="number"
                      min="15"
                      max="480"
                      step="15"
                      value={durationMinutes}
                      onChange={(event) => setDurationMinutes(event.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row className="g-3">
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Lead Time Days</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      max="30"
                      value={leadTimeDays}
                      onChange={(event) => setLeadTimeDays(event.target.value)}
                    />
                  </Form.Group>
                </Col>

                <Col md={2}>
                  <Form.Group className="mb-3">
                    <Form.Label>Order</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={order}
                      onChange={(event) => setOrder(event.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Pricing Options</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={pricingOptionsText}
                  onChange={(event) => setPricingOptionsText(event.target.value)}
                  placeholder="key | label | price | unit label | min qty | max qty | default | image URL | backs | included qty | included label"
                />
              </Form.Group>

              <div className="d-flex gap-4 flex-wrap mb-4">
                <Form.Check
                  type="checkbox"
                  label="Published"
                  checked={isPublished}
                  onChange={(event) => setIsPublished(event.target.checked)}
                />

                <Form.Check
                  type="checkbox"
                  label="Orderable online"
                  checked={orderable}
                  onChange={(event) => setOrderable(event.target.checked)}
                />

                {isEdit ? (
                  <Form.Check
                    type="checkbox"
                    label="Deleted"
                    checked={isDeleted}
                    onChange={(event) => setIsDeleted(event.target.checked)}
                  />
                ) : null}
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <Button type="submit" variant="dark" disabled={busy}>
                  {busy ? "Saving..." : isEdit ? "Save Service" : "Create Service"}
                </Button>
                <Link to="/admin/services" className="btn btn-outline-dark">
                  Cancel
                </Link>
              </div>
            </Card.Body>
          </Card>
        </Form>
      )}
    </Container>
  );
}
